import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { type LayoutChangeEvent, Platform, useWindowDimensions } from "react-native";
import { Dialog, ScrollView, styled, YStack } from "tamagui";
import { shadow } from "../../themes/token-shadow";
import { NavHeader } from "../NavHeader";
import { ModalFooter, modalPaddingBySize } from "./ModalParts";
import type { ModalFooterProps, ModalHeaderProps, ModalSize } from "./types";
export const modalMaxWidthBySize = {
  small: 440,
  medium: 666,
  large: 892
} as const;
const DEFAULT_MAX_HEIGHT = 800;
const desktopHeaderPaddingBySize = {
  small: {
    paddingVertical: "$sp1",
    paddingHorizontal: "$sp0"
  },
  medium: {
    paddingVertical: "$sp1",
    paddingHorizontal: "$sp1"
  },
  large: {
    paddingVertical: "$sp2",
    paddingHorizontal: "$sp2"
  }
} as const;
const StyledDialogContent = styled(Dialog.Content, {
  name: "DialogContent",
  backgroundColor: "$background",
  overflow: "hidden",
  padding: 0,
  // Use CSS transitions instead of Tamagui's animation system to avoid inline styles
  style: {
    transitionProperty: "transform, opacity",
    transitionDuration: "200ms",
    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)"
  },
  variants: {
    modalSize: {
      small: {
        width: "90%",
        maxWidth: modalMaxWidthBySize.small,
        borderRadius: "$br3"
      },
      medium: {
        width: "90%",
        maxWidth: modalMaxWidthBySize.medium,
        borderRadius: "$br3"
      },
      large: {
        width: "90%",
        maxWidth: modalMaxWidthBySize.large,
        borderRadius: "$br3"
      }
    },
    fullScreen: {
      true: {
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        borderRadius: "$br0"
      }
    },
    shadow
  } as const,
  defaultVariants: {
    modalSize: "large",
    shadow: "heavy"
  }
});

/**
 * Web-only component that animates height changes using CSS transitions.
 * When maxHeight is provided, content will scroll if it exceeds that height.
 */
function AnimatedHeight({
  children,
  maxHeight
}: {
  children: ReactNode;
  maxHeight?: number;
}) {
  const [height, setHeight] = useState<number | null>(null);
  const [naturalHeight, setNaturalHeight] = useState<number | null>(null);
  const lastHeightRef = useRef<number | null>(null);
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const newHeight = event.nativeEvent.layout.height;
    setNaturalHeight(newHeight);
    const cappedHeight = maxHeight !== undefined ? Math.min(newHeight, maxHeight) : newHeight;
    if (lastHeightRef.current !== cappedHeight) {
      lastHeightRef.current = cappedHeight;
      setHeight(cappedHeight);
    }
  }, [maxHeight]);
  const needsScroll = maxHeight !== undefined && naturalHeight !== null && naturalHeight > maxHeight;
  return <YStack height={height ?? undefined} overflow="hidden" style={{
    transition: height !== null ? "height 300ms cubic-bezier(0.16, 1, 0.3, 1)" : undefined,
    overflow: needsScroll ? "auto" : undefined
  }} data-sentry-element="YStack" data-sentry-component="AnimatedHeight" data-sentry-source-file="DialogModal.tsx">
      <YStack onLayout={onLayout} data-sentry-element="YStack" data-sentry-source-file="DialogModal.tsx">{children}</YStack>
    </YStack>;
}
export type DialogModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  headerProps: ModalHeaderProps;
  footerProps?: ModalFooterProps;
  size?: ModalSize;
  fullScreen?: boolean;
  scrollable?: boolean;
  /** Animate height changes (web only) */
  animateHeight?: boolean;
  /** Max content height when animateHeight is true */
  maxContentHeight?: number;
};

/**
 * Dialog Modal component for web and native tablets/desktops.
 * Renders a centered dialog with header and footer derived from props.
 *
 * @example
 * <DialogModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   headerProps={{ title: "Settings" }}
 *   footerProps={{ primaryButtonProps: { children: "Save" } }}
 * >
 *   <Text>Content</Text>
 * </DialogModal>
 */
export const DialogModal = ({
  open,
  onOpenChange,
  children,
  headerProps,
  footerProps,
  size = "medium",
  fullScreen = false,
  scrollable = true,
  animateHeight = true,
  maxContentHeight = DEFAULT_MAX_HEIGHT
}: DialogModalProps) => {
  const {
    height: windowHeight
  } = useWindowDimensions();

  // Calculate max height: 100% of screen - 200px (100px top + 100px bottom)
  const maxHeight = useMemo(() => {
    if (fullScreen) {
      return undefined;
    }
    return windowHeight - 200;
  }, [windowHeight, fullScreen]);
  const renderContent = () => {
    const horizontalPadding = modalPaddingBySize[size].horizontal;
    const paddingBottom = footerProps ? undefined : modalPaddingBySize[size].footerVertical;
    const innerContent = animateHeight && !fullScreen ? <AnimatedHeight maxHeight={maxContentHeight}>{children}</AnimatedHeight> : children;
    const paddedContent = <YStack flex={1} paddingHorizontal={horizontalPadding} paddingBottom={paddingBottom}>
        {innerContent}
      </YStack>;
    if (scrollable) {
      return <ScrollView flex={1} contentContainerStyle={{
        flexGrow: 1,
        minHeight: "100%"
      }}>
          {paddedContent}
        </ScrollView>;
    }
    return paddedContent;
  };
  return <Dialog open={open} onOpenChange={onOpenChange} modal data-sentry-element="Dialog" data-sentry-component="DialogModal" data-sentry-source-file="DialogModal.tsx">
      <Dialog.Portal zIndex="$modal" data-sentry-element="Dialog.Portal" data-sentry-source-file="DialogModal.tsx">
        <Dialog.Overlay key="overlay" backgroundColor="$backgroundSecondary" opacity={0.5} style={{
        transitionProperty: "opacity",
        transitionDuration: "200ms",
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        ...(Platform.OS === "web" && {
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)"
        })
      }} data-sentry-element="Dialog.Overlay" data-sentry-source-file="DialogModal.tsx" />
        <StyledDialogContent key="content" modalSize={fullScreen ? undefined : size} fullScreen={fullScreen || undefined} maxHeight={maxHeight} data-sentry-element="StyledDialogContent" data-sentry-source-file="DialogModal.tsx">
          <YStack width="100%" flex={1} data-sentry-element="YStack" data-sentry-source-file="DialogModal.tsx">
            {/* Dialog modal header padding: fullScreen uses small; desktop uses size-based padding */}
            <YStack paddingVertical={fullScreen ? desktopHeaderPaddingBySize.small.paddingVertical : desktopHeaderPaddingBySize[size].paddingVertical} paddingHorizontal={fullScreen ? desktopHeaderPaddingBySize.small.paddingHorizontal : desktopHeaderPaddingBySize[size].paddingHorizontal} data-sentry-element="YStack" data-sentry-source-file="DialogModal.tsx">
              <NavHeader {...headerProps} onClosePress={headerProps.onClosePress ?? (() => onOpenChange(false))} isDialogHeader data-sentry-element="NavHeader" data-sentry-source-file="DialogModal.tsx" />
            </YStack>
            {renderContent()}
            {footerProps ? <ModalFooter {...footerProps} size={size} isNativeSheet={false} /> : null}
          </YStack>
        </StyledDialogContent>
      </Dialog.Portal>
    </Dialog>;
};